import { createCable } from "@anycable/web";
import { stringifyParams } from "@anycable/core";

class EmotiiWebSocket {
  static instance = null;

  constructor() {
    if (EmotiiWebSocket.instance) {
      return EmotiiWebSocket.instance;
    }

    this.cable = null;
    this.subscriptions = new Map(); // Track active subscriptions
    this.dataCallbacks = []; // Track callbacks for each channel
    this.subscribedChannels = new Map(); // Track channels to re-subscribe on reconnect
    this.channelKeys = new Map(); // Track channel identifier keys
    this.connectListeners = new Set();

    EmotiiWebSocket.instance = this;
  }

  connect(disconnectCallback) {
    const endpoint = sessionStorage.getItem("wsLink");

    if (!endpoint) {
      return;
    }

    this.disconnect(); // Clean up before reconnecting
    this.cable = createCable(endpoint);

    this.cable
      .connect()
      .then(() => {
        console.log("Connected to AnyCable");
        this.#connectToChannelsIfNeeded(); // Re-subscribe to channels
      })
      .catch((error) => {
        console.error("Connection failed:", error);
        this.#handleSocketClosed();

        // Disconnected
        disconnectCallback();
      });

    // Listen for reconnection events
    this.cable.on("connect", () => {
      console.log("Reconnected to AnyCable");
      this.#connectToChannelsIfNeeded(); // Re-subscribe to channels
      this.connectListeners.forEach((listener) => listener());
    });

    // Listen for disconnection events
    this.cable.on("disconnect", () => {
      console.log("Disconnected from AnyCable");
      this.#handleSocketClosed();

      // Disconnected
      disconnectCallback();
    });
  }

  addConnectListener(listener) {
    this.connectListeners.add(listener);
    return () => this.connectListeners.delete(listener);
  }

  disconnect() {
    if (this.cable) {
      this.cable.disconnect();
      this.cable = null;
    }
    this.#removeAllSubscriptions(); // Clean up subscriptions
  }

  isConnected() {
    if (!this.cable || this.cable.state !== "connected") {
      return false;
    }

    return true;
  }

  subscribeToChannel(name, callback, params = {}) {
    const existingCallback = this.dataCallbacks.find(
      (item) => item.name === name
    );

    if (existingCallback) {
      if (callback && existingCallback.callback !== callback) {
        existingCallback.callback = callback;
      }
    } else if (callback) {
      const item = { name, callback };
      this.dataCallbacks.push(item);
    }

    const existingParams = this.subscribedChannels.get(name);
    const nextParams =
      existingParams && Object.keys(existingParams).length > 0
        ? existingParams
        : params || {};

    this.subscribedChannels.set(name, nextParams); // Track the channel for re-subscription

    if (!this.cable || this.cable.state !== "connected") {
      return;
    }

    this.#connectToChannel(name, nextParams);
  }

  updateChannelParams(name, params = {}) {
    this.subscribedChannels.set(name, params || {});

    if (!this.cable || this.cable.state !== "connected") {
      return;
    }

    this.#connectToChannel(name, params);
  }

  unsubscribeFromChannel(name, params = null) {
    const channelKey = params
      ? this.#makeChannelKey(name, params)
      : this.channelKeys.get(name);

    if (!channelKey) {
      return;
    }

    this.#unsubscribeByKey(channelKey);

    if (this.channelKeys.get(name) === channelKey) {
      this.channelKeys.delete(name);
      this.subscribedChannels.delete(name);
    }
  }

  async sendMessage(channelName, dataObject) {
    if (!this.cable || this.cable.state !== "connected") {
      return;
    }

    if (this.#isConferenceTranslationModeUpdate(channelName, dataObject)) {
      const isReadyForTargetCall =
        this.#hasReadyConferenceSubscriptionForCall(dataObject?.call_uuid);

      if (!isReadyForTargetCall) {
        this.#retryConferenceTranslationSend(channelName, dataObject, 0);
        return;
      }
    }

    const channelKey = this.channelKeys.get(channelName);
    const subscription = channelKey
      ? this.subscriptions.get(channelKey)
      : this.subscriptions.get(channelName);
    if (subscription) {
      try {
        await subscription.perform("receive", dataObject); // Ensure it's not blocked or failed
      } catch (error) {
        console.error(`Failed to send message to ${channelName}:`, error);
      }
    } else if (this.#isConferenceTranslationModeUpdate(channelName, dataObject)) {
      this.#retryConferenceTranslationSend(channelName, dataObject, 0);
    }
  }

  // Private methods

  #isConferenceTranslationModeUpdate(channelName, dataObject) {
    if (channelName !== "ConferenceCallChannel" || !dataObject) {
      return false;
    }

    const hasEnabledFlag = Object.prototype.hasOwnProperty.call(
      dataObject,
      "enabled"
    );
    const hasTranslationTypeField = Object.prototype.hasOwnProperty.call(
      dataObject,
      "translation_type"
    );

    return hasEnabledFlag && hasTranslationTypeField && !!dataObject.call_uuid;
  }

  #hasReadyConferenceSubscriptionForCall(callUuid) {
    if (!callUuid) {
      return false;
    }

    const normalizedTargetCallUuid = String(callUuid).toLowerCase();
    const subscribedParams = this.subscribedChannels.get("ConferenceCallChannel") || {};
    const subscribedCallUuid = subscribedParams?.call_uuid
      ? String(subscribedParams.call_uuid).toLowerCase()
      : null;
    const activeChannelKey = this.channelKeys.get("ConferenceCallChannel");
    const expectedChannelKey = this.#makeChannelKey(
      "ConferenceCallChannel",
      subscribedParams
    );

    if (!subscribedCallUuid || subscribedCallUuid !== normalizedTargetCallUuid) {
      return false;
    }

    if (!activeChannelKey || activeChannelKey !== expectedChannelKey) {
      return false;
    }

    return this.subscriptions.has(activeChannelKey);
  }

  #retryConferenceTranslationSend(channelName, dataObject, attempt = 0) {
    if (!this.#isConferenceTranslationModeUpdate(channelName, dataObject)) {
      return;
    }

    if (attempt >= 8) {
      console.warn(
        "[WebSocket] Conference translation mode send dropped: channel subscription not ready",
        {
          channelName,
          callUuid: dataObject?.call_uuid,
          attempt,
          subscribedConferenceParams:
            this.subscribedChannels.get("ConferenceCallChannel") || null,
        }
      );
      return;
    }

    const delayMs = attempt === 0 ? 120 : 150;

    setTimeout(() => {
      if (!this.cable || this.cable.state !== "connected") {
        return;
      }

      if (!this.#hasReadyConferenceSubscriptionForCall(dataObject?.call_uuid)) {
        this.#retryConferenceTranslationSend(channelName, dataObject, attempt + 1);
        return;
      }

      this.sendMessage(channelName, dataObject);
    }, delayMs);
  }

  #connectToChannelsIfNeeded() {
    if (!this.cable || this.cable.state !== "connected") {
      return;
    }

    // Re-subscribe to all tracked channels
    this.subscribedChannels.forEach((params, channelName) => {
      this.#connectToChannel(channelName, params);
    });
  }

  #connectToChannel(name, params = {}) {
    if (!this.cable || this.cable.state !== "connected") {
      return;
    }

    const channelKey = this.#makeChannelKey(name, params);
    const existingKey = this.channelKeys.get(name);

    if (existingKey && existingKey !== channelKey) {
      this.#unsubscribeByKey(existingKey);
    }

    // Avoid duplicate subscriptions
    if (this.subscriptions.has(channelKey)) {
      return;
    }

    let subscription = this.cable.subscribeTo(name, params);
    subscription.on("message", (data) => {
      this.#handleMessageForChannel(name, data);
    });

    this.subscriptions.set(channelKey, subscription);
    this.channelKeys.set(name, channelKey);
  }

  #handleMessageForChannel(channel, message) {
    const item = this.dataCallbacks.find((obj) => obj.name === channel);
    if (item) {
      let payload = message;
      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch (e) {
          payload = message;
        }
      }
      item.callback(channel, payload);
    }
  }

  #handleSocketClosed() {
    if (!this.cable || this.cable.state === "connected") {
      return;
    }

    console.log("Socket closed, cleaning up subscriptions...");
    this.#removeAllSubscriptions(); // Clean up subscriptions on close
  }

  #removeAllSubscriptions() {
    this.subscriptions.forEach((subscription, channelKey) => {
      this.#unsubscribeByKey(channelKey);
    });
    this.subscriptions.clear();
    this.channelKeys.clear();
  }

  #makeChannelKey(name, params = {}) {
    return stringifyParams({ channel: name, ...(params || {}) });
  }

  #unsubscribeByKey(channelKey) {
    const subscription = this.subscriptions.get(channelKey);
    if (!subscription) {
      return;
    }

    try {
      subscription.disconnect();
    } catch (err) {
      console.warn("Failed to disconnect subscription", channelKey, err);
    }

    this.subscriptions.delete(channelKey);
  }
}

export default EmotiiWebSocket;
